import React from "react";
import { useNavigate } from "react-router-dom";
import "./AuditForm.css";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { getCountries, Country } from "../../../api/country/countryapi";
import { getPlatforms, Platform } from "../../../api/platform/platformapi";

type AuditFormProps = {
  type?: "web" | "social";
  countryCode: string;
  setCountryCode: React.Dispatch<React.SetStateAction<string>>;
  onSubmit?: (data: Record<string, string>) => void;
};

// Per-platform example placeholder. The backend accepts either a handle or a
// full URL (it strips a leading @ and detects URLs), so we only guide the user
// with a correct example — no hard cleaning on the frontend.
//   - Facebook / LinkedIn: no "@" (LinkedIn needs the full URL: it can't guess
//     between /company/ and /in/).
//   - Instagram / X: "@" is conventional but optional.
const getHandlePlaceholder = (platform: string): string => {
  const p = platform.trim().toLowerCase();

  if (p.includes("facebook")) return "e.g. aigio or facebook.com/aigio";
  if (p.includes("instagram")) return "e.g. @aigio or instagram.com/aigio";
  if (p.includes("linkedin")) return "e.g. linkedin.com/company/aigio";
  if (p === "x" || p.includes("twitter") || p.includes("x.com"))
    return "e.g. @aigio or x.com/aigio";

  return "Enter handle or profile URL";
};

const AuditForm: React.FC<AuditFormProps> = ({
  type = "social",
  countryCode,
  setCountryCode,
  onSubmit,
}) => {
  const isWeb = type === "web";
  const navigate = useNavigate();
 
 
  // Go back to the page the user came from. Falls back to the landing page
  // when there's no in-app history (e.g. /audit was opened directly).
  const handleCancel = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

 const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault();

  const formData = new FormData(event.currentTarget);

  const data = Object.fromEntries(formData.entries()) as Record<
    string,
    string
  >;

  onSubmit?.({
    ...data,
    countryCode,
  });
};
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  // ✅ Correct
  const [countries, setCountries] = useState<Country[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [platformSearch, setPlatformSearch] = useState("");

  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [platformOpen, setPlatformOpen] = useState(false);

  const platformDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        platformDropdownRef.current &&
        !platformDropdownRef.current.contains(event.target as Node)
      ) {
        setPlatformOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const fetchPlatforms = async (search = "") => {
    try {
      const data = await getPlatforms(search);
      setPlatforms(data);
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    if (!platformOpen) return;

    const timer = setTimeout(() => {
      fetchPlatforms(platformSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [platformSearch, platformOpen]);
  useEffect(() => {
    if (!isWeb) {
      fetchPlatforms();
    }
  }, [isWeb]);
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countryList = await getCountries();
        setCountries(countryList);

        const india = countryList.find(
          (country) => country.country_code === "+91",
        );

        if (india) {
          setCountryCode(india.country_code);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchCountries();
  }, [setCountryCode]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
   <>
    <form
      className={`audit-form ${
        countryCode === "+91"
          ? "audit-form--india"
          : "audit-form--international"
      }`}
      onSubmit={handleSubmit}
      autoComplete="off"
    >
      <div className="audit-form__body">
        {/* ================= Identity ================= */}
        <div className="audit-form__section-block">
          <div className="section-header">
            <p className="section-title">Identity</p>
            <h2 className="section-subtitle">
              PRIMARY CONTACT INFORMATION FOR THE AUDIT REPORT
            </h2>
          </div>

          <div className="audit-form__rows">
            <div className="audit-form__row audit-form__row--two-up">
              <label className="audit-form__field">
                <span>Full name</span>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Enter your full name"
                  required
                  autoComplete="off"
                  pattern="[A-Za-z ]+"
                  title="Full name must contain letters only"
                />
              </label>

              <label className="audit-form__field">
                <span>Email id</span>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  required
                  autoComplete="off"
                />
              </label>
            </div>

            <div className="audit-form__row">
              <label className="audit-form__field audit-form__field--phone">
                <span>Phone number</span>

                <div className="audit-form__phone-group">
                  <div
                    className="custom-dropdown"
                    ref={dropdownRef}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="custom-dropdown__selected"
                      onClick={() => setOpen((prev) => !prev)}
                    >
                      <span>{countryCode || "Select code"}</span>

                      <ChevronDown
                        size={16}
                        className={`custom-dropdown__arrow ${open ? "open" : ""}`}
                      />
                    </div>

                    {open && (
                      <div className="custom-dropdown__menu">
                        <input
                          className="custom-dropdown__search"
                          autoFocus
                          autoComplete="off"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search country code..."
                        />

                        <div className="custom-dropdown__list">
                          {countries
                            .filter((c) =>
                              c.country_code
                                .toLowerCase()
                                .includes(search.toLowerCase()),
                            )
                            .map((country) => (
                              <div
                                key={country.id}
                                className="custom-dropdown__item"
                                onClick={() => {
                                  setCountryCode(country.country_code);
                                  setOpen(false);
                                  setSearch("");
                                }}
                              >
                                {country.country_code}
                              </div>
                            ))}

                          {countries.filter((c) =>
                            c.country_code
                              .toLowerCase()
                              .includes(search.toLowerCase()),
                          ).length === 0 && (
                            <div className="custom-dropdown__empty">
                              No results
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="Enter your phone number"
                    autoComplete="off"
                    inputMode="numeric"
                    maxLength={10}
                    pattern="[0-9]{10}"
                    title="Enter a valid 10-digit phone number"
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                    }}
                  />
                </div>
              </label>
            </div>

            {countryCode === "+91" && (
              <div className="audit-form__row audit-form__row--two-up">
                <label className="audit-form__field">
                  <span>City</span>
                  <input
                    type="text"
                    name="city"
                    required
                    placeholder="Enter your city"
                    autoComplete="off"
                    pattern="^[A-Za-z]+(?: [A-Za-z]+)*$"
                    title="City can contain only letters"
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(
                        /[^A-Za-z ]/g,
                        "",
                      );
                    }}
                  />
                </label>

                <label className="audit-form__field">
                  <span>State</span>
                  <input
                    type="text"
                    name="state"
                    required
                    placeholder="Enter your state"
                    autoComplete="off"
                    pattern="^[A-Za-z]+(?: [A-Za-z]+)*$"
                    title="State can contain only letters"
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(
                        /[^A-Za-z ]/g,
                        "",
                      );
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* ================= Target ================= */}
        <div className="audit-form__section-block">
          <div className="section-header">
            <p className="section-title">
              {isWeb ? "Web target" : "Social target"}
            </p>

            <h3 className="section-subtitle">
              PRIMARY CONTACT INFORMATION FOR THE AUDIT REPORT
            </h3>
          </div>

          <div className="audit-form__rows">
            {isWeb ? (
              <div className="audit-form__row">
                <label className="audit-form__field">
                  <span>Website URL</span>

                  <input
                    type="url"
                    name="websiteUrl"
                    placeholder="Enter your website URL"
                    required
                    autoComplete="off"
                  />
                </label>
              </div>
            ) : (
              <div className="audit-form__row audit-form__row--two-up">
                <label className="audit-form__field">
                  <span>Platform</span>

                  <div
                    className="platform-dropdown"
                    ref={platformDropdownRef}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="platform-dropdown__selected"
                      onClick={() => {
                        setPlatformOpen((prev) => !prev);

                        if (!platformOpen) {
                          fetchPlatforms();
                        }
                      }}
                    >
                      <span>{selectedPlatform || "SELECT PLATFORM"}</span>

                      <ChevronDown
                        size={16}
                        className={`platform-dropdown__arrow ${
                          platformOpen ? "open" : ""
                        }`}
                      />
                    </div>

                    {platformOpen && (
                      <div className="platform-dropdown__menu">
                        <input
                          className="platform-dropdown__search"
                          value={platformSearch}
                          placeholder="Search platform..."
                          autoFocus
                          autoComplete="off"
                          onChange={(e) => setPlatformSearch(e.target.value)}
                        />

                        <div className="platform-dropdown__list">
                          {platforms.map((platform) => (
                            <div
                              key={platform.id}
                              className="platform-dropdown__item"
                              onClick={() => {
                                setSelectedPlatform(platform.platform_name);
                                setPlatformOpen(false);
                                setPlatformSearch("");
                              }}
                            >
                              {platform.platform_name}
                            </div>
                          ))}

                          {platforms.length === 0 && (
                            <div className="platform-dropdown__empty">
                              No platforms found
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <input
                      type="hidden"
                      name="platform"
                      value={selectedPlatform}
                      required
                    />
                  </div>
                </label>

                <label className="audit-form__field">
                  <span>Handle/URL</span>

                  <div className="audit-form__handle-field">
                    <input
                      type="text"
                      name="handle"
                      placeholder={getHandlePlaceholder(selectedPlatform)}
                      required
                      autoComplete="off"
                    />
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* ================= Pain Points ================= */}
        <div className="audit-form__section-block">
          <div className="section-header">
            <p className="section-title">Pain Points</p>

            <h3 className="section-subtitle">
              WHAT CHALLENGES ARE YOU CURRENTLY FACING
            </h3>
          </div>

          <div className="audit-form__rows">
            <div className="audit-form__row">
              <label className="audit-form__field audit-form__field--textarea">
                <textarea
                  name="painPoints"
                  rows={4}
                  placeholder="Describe your current roadblocks, engagement plateaus, or content strategy hurdles"
                  required
                  autoComplete="off"
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="audit-form__actions">
        <button
          type="button"
          className="audit-form__button audit-form__button--ghost"
          onClick={handleCancel}
        >
          <ArrowLeft size={16} />
          <div className="audit_button_text">CANCEL</div>
        </button>

        <button
          type="submit"
          className="audit-form__button audit-form__button--primary"
        >
          <div className="audit_button_text">Join Waitlist</div>
          <ArrowRight size={16} />
        </button>
      </div>
    </form>
   
  </>
);
};
export default AuditForm;
